import {
  MouseEvent,
  WheelEvent,
  useRef,
  useEffect,
  KeyboardEvent,
} from 'react';
import {
  drawTemporaryMarker,
  drawMask,
  drawMonitor,
  drawThreshold,
  pixelToIndex,
} from 'lib/lpt-charts/spectrumChart';
import {
  formatMarker,
  formatMask,
  formatMonitor,
  formatThreshold,
} from '../utils/pixelsToType';

import { useCreateMonitor } from 'features/monitors/api/createMonitor';
import { useUpdateMonitor } from 'features/monitors/api/updateMonitor';
import { useCreateThreshold } from 'features/thresholds/api/createThreshold';
import { useUpdateThreshold } from 'features/thresholds/api/updateThreshold';
import { useCreateMask } from 'features/spectralMasks/api/createMask';
import { useUpdateMask } from 'features/spectralMasks/api/updateMask';
import { InterferenceDetector } from 'types/shared/InterferenceDetector';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { markerMoved } from 'features/markers/markersSlice';

interface Props {
  canvas: HTMLCanvasElement | null;
  drawingMode:
    | {
        isDrawing: boolean;
        drawingType: InterferenceDetector;
        redrawId: number;
        name?: string | undefined;
      }
    | undefined;
  drawingFinished?: () => void;
  snapshotId: number;
  snapshotFrequencies: string[];
  centerUnit: string;
  reference: number;
  scale: number;
  traceLength: number | undefined;
}

export interface CanvasPoint {
  x: number;
  y: number;
}

export const useDrawing = ({
  canvas,
  drawingMode,
  drawingFinished,
  snapshotId,
  snapshotFrequencies,
  centerUnit,
  reference,
  scale,
  traceLength,
}: Props) => {
  const dispatch = useAppDispatch();
  const ctx: CanvasRenderingContext2D | null | undefined =
    canvas?.getContext('2d');

  const createMonitorMutation = useCreateMonitor({ snapshotId });
  const updateMonitorMutation = useUpdateMonitor({ snapshotId });
  const createMaskMutation = useCreateMask({ snapshotId });
  const updateMaskMutation = useUpdateMask({ snapshotId });
  const createThresholdMutation = useCreateThreshold({ snapshotId });
  const updateThresholdMutation = useUpdateThreshold({ snapshotId });

  const isDrawing = useRef<boolean>(false);
  const maskBounds = useRef<number>(10);
  const startingPoint = useRef<CanvasPoint>({ x: 0, y: 0 });
  const currentPoint = useRef<CanvasPoint>({ x: 0, y: 0 });

  const selectedMarkerIndex = useAppSelector(
    (state) => state.markers.selectedMarker
  );

  useEffect(() => {
    if (drawingFinished) {
      drawingFinished();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!canvas) return;
    if (drawingMode?.isDrawing) canvas.style.cursor = 'crosshair';
    else canvas.style.cursor = 'default';
  }, [drawingMode?.isDrawing, canvas]);

  const drawingMouseLeave = (event: MouseEvent<HTMLCanvasElement>) => {
    if (!ctx) return;
    currentPoint.current.x = 0;
    currentPoint.current.y = 0;
  };

  const drawingMouseDown = (event: MouseEvent<HTMLCanvasElement>) => {
    if (!drawingMode?.isDrawing || !ctx) return;
    maskBounds.current = 10;
    startingPoint.current.x = event.nativeEvent.offsetX;
    startingPoint.current.y = event.nativeEvent.offsetY;

    isDrawing.current = true;

    if (drawingMode.drawingType === InterferenceDetector.Marker) {
      handleMarkerMoved();
    }
  };

  const drawingMouseMove = (event: MouseEvent<HTMLCanvasElement>) => {
    if (!ctx) return;
    currentPoint.current.x = event.nativeEvent.offsetX;
    currentPoint.current.y = event.nativeEvent.offsetY;

    if (!isDrawing.current || !drawingMode) return;

    if (drawingMode.drawingType === InterferenceDetector.Marker) {
      handleMarkerMoved();
    }
  };

  const drawingMouseUp = (event: MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing.current || !ctx) return;
    formatAndSave();
    isDrawing.current = false;
    startingPoint.current = { x: 0, y: 0 };
    currentPoint.current = { x: 0, y: 0 };
    if (drawingFinished) {
      drawingFinished();
    }
  };

  const cancelDrawing = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      startingPoint.current = { x: 0, y: 0 };
      currentPoint.current = { x: 0, y: 0 };
      isDrawing.current = false;
      if (drawingFinished) {
        drawingFinished();
      }
    }
  };

  const handleMarkerMoved = () => {
    if (!ctx) return;

    const marker = {
      index: selectedMarkerIndex,
      x_pos: pixelToIndex(ctx, currentPoint.current.x, traceLength || 601),
      snapshot_id: snapshotId,
    };

    dispatch(markerMoved(marker));
  };

  const drawingMouseWheel = (event: WheelEvent<HTMLCanvasElement>) => {
    if (event.deltaY === 100 && maskBounds.current !== 0) {
      maskBounds.current--;
    } else {
      maskBounds.current++;
    }
  };

  const renderDrawing = (
    traceData: number[],
    baseline: number[],
    pixelTrace: number[],
    isDotted: boolean
  ) => {
    if (!ctx || (currentPoint.current.x === 0 && currentPoint.current.y === 0))
      return;

    //Draw temporary marker on mouse over
    if (!isDrawing.current) {
      drawTemporaryMarker(
        ctx,
        currentPoint.current.x,
        traceData,
        snapshotFrequencies,
        centerUnit,
        reference,
        scale
      );
      return;
    }

    switch (drawingMode?.drawingType) {
      case InterferenceDetector.Monitor:
        drawMonitor(
          ctx,
          drawingMode?.name ? drawingMode.name : 'New Monitor',
          startingPoint.current.x,
          startingPoint.current.y,
          currentPoint.current.x,
          currentPoint.current.y,
          pixelTrace,
          false
        );
        break;
      case InterferenceDetector.Mask:
        drawMask(
          ctx,
          '',
          baseline,
          startingPoint.current.x,
          currentPoint.current.x,
          maskBounds.current,
          maskBounds.current,
          true,
          traceData,
          reference,
          scale
        );
        break;
      case InterferenceDetector.Threshold:
        drawThreshold(
          ctx,
          drawingMode?.name ? drawingMode.name : 'New Threshold',
          startingPoint.current.x,
          startingPoint.current.y,
          currentPoint.current.x,
          pixelTrace,
          isDotted
        );
        break;
    }
  };

  const formatAndSave = () => {
    switch (drawingMode?.drawingType) {
      case InterferenceDetector.Monitor:
        const monitor = formatMonitor(
          ctx,
          traceLength,
          reference,
          scale,
          startingPoint.current,
          currentPoint.current
        );
        //Checks if a monitor exists or if a 0x0 area monitor is being created.
        if (
          !monitor ||
          (startingPoint.current.x === currentPoint.current.x &&
            startingPoint.current.y === currentPoint.current.y)
        )
          return;

        if (drawingMode.redrawId > -1) {
          const monitorWithName = {
            ...monitor,
            name: drawingMode.name,
          };
          updateMonitorMutation.mutate({
            data: monitorWithName,
            monitorId: drawingMode.redrawId,
            snapshotId,
          });
        } else {
          createMonitorMutation.mutate({
            data: monitor,
            snapshotId,
          });
        }

        break;
      case InterferenceDetector.Marker:
        const marker = formatMarker(
          ctx,
          traceLength,
          currentPoint.current,
          selectedMarkerIndex
        );
        if (!marker) return;
        break;
      case InterferenceDetector.Mask:
        const mask = formatMask(
          ctx,
          traceLength,
          startingPoint.current,
          currentPoint.current,
          maskBounds.current
        );
        if (!mask) return;

        if (drawingMode.redrawId > -1) {
          const maskWithName = {
            ...mask,
            name: drawingMode.name,
          };

          updateMaskMutation.mutate({
            data: maskWithName,
            maskId: drawingMode.redrawId,
            snapshotId,
          });
        } else {
          createMaskMutation.mutate({
            data: mask,
            snapshotId,
          });
        }

        break;
      case InterferenceDetector.Threshold:
        const threshold = formatThreshold(
          ctx,
          traceLength,
          reference,
          scale,
          startingPoint.current,
          currentPoint.current
        );
        if (!threshold || threshold.begin === threshold.end) return;

        if (threshold.begin > threshold.end) {
          const temp = threshold.begin;
          threshold.begin = threshold.end;
          threshold.end = temp;
        }

        if (drawingMode.redrawId > -1) {
          const thresholdWithName = {
            ...threshold,
            name: drawingMode.name,
          };

          updateThresholdMutation.mutate({
            data: thresholdWithName,
            thresholdId: drawingMode.redrawId,
            snapshotId,
          });
        } else {
          createThresholdMutation.mutate({
            data: threshold,
            snapshotId,
          });
        }

        break;
    }
  };

  return {
    drawingMouseDown,
    drawingMouseMove,
    drawingMouseUp,
    drawingMouseWheel,
    drawingMouseLeave,
    cancelDrawing,
    renderDrawing,
  };
};
